import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth.store';
import { notificationStore } from '@/stores/notification.store';

const API_BASE_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/api/v1`;

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor — attach token, short-circuit on known expiry.
//
// If the auth store says the access token is past its expiresAt AND we
// have no refresh token to recover with, fire a logout-and-redirect now
// instead of sending a doomed request. This avoids the "wake up tab,
// see error JSON, then bounce to /login" UX that happens when the user
// leaves the browser idle past the JWT lifetime.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (!token) return config;

    const auth = useAuthStore.getState();
    const definitelyExpired = auth.expiresAt !== null && Date.now() > auth.expiresAt;
    if (definitelyExpired && !auth.refreshToken && !auth.isBypassMode) {
      auth.clearAuth();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(new axios.Cancel('Session expired'));
    }

    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean; _retry503?: boolean };

    // Handle 403 Forbidden — show notification
    if (error.response?.status === 403) {
      const data = error.response.data as Record<string, unknown> | undefined;
      const msg = (data?.error as Record<string, string>)?.message
        || (data?.message as string)
        || 'You do not have permission to perform this action.';
      notificationStore.getState().show(msg, 'error');
      return Promise.reject(error);
    }

    // Handle 503 Service Unavailable.
    //
    // Common cause: the Casdoor permission cache (5min TTL) expires while
    // the UI sits idle, the first request after wake-up misses the cache,
    // and a single Casdoor API blip yields "Unable to verify permissions".
    // The next request usually succeeds because the cache gets repopulated.
    //
    // Strategy: retry idempotent GETs once silently with a short backoff,
    // then surface the toast if it fails again. Mutations are never retried
    // automatically — the caller decides.
    if (error.response?.status === 503) {
      const method = (originalRequest.method || 'get').toLowerCase();
      const retryable = method === 'get' && !originalRequest._retry503;
      if (retryable) {
        originalRequest._retry503 = true;
        await new Promise((r) => globalThis.setTimeout(r, 800));
        return api(originalRequest);
      }
      const data = error.response.data as Record<string, unknown> | undefined;
      const msg = (data?.error as Record<string, string>)?.message
        || (data?.detail as string)
        || (data?.message as string)
        || 'Service temporarily unavailable. Please try again.';
      notificationStore.getState().show(msg, 'warning');
      return Promise.reject(error);
    }

    // If 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry auth endpoints
      if (originalRequest.url?.includes('/auth/')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until refresh is complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');

      if (!refreshToken) {
        // No refresh token, logout
        isRefreshing = false;
        useAuthStore.getState().clearAuth();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Try to refresh the token
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const { access_token, refresh_token, expires_in, user } = response.data;

        // Update auth store
        useAuthStore.getState().setAuth({
          access_token,
          refresh_token,
          expires_in,
          token_type: 'Bearer',
          user,
        });

        isRefreshing = false;
        processQueue(null, access_token);

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout
        isRefreshing = false;
        processQueue(refreshError, null);
        useAuthStore.getState().clearAuth();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Generic request function with better typing
export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await api.request<T>(config);
  return response.data;
}

// Convenience methods
export const apiClient = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'GET', url }),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'POST', url, data }),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'PUT', url, data }),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'PATCH', url, data }),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'DELETE', url }),
};

export default apiClient;
